import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, 
  Send, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Clock,
  ArrowRight,
  Check
} from 'lucide-react';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      icon: FileText,
      title: 'Crea Presupuestos',
      description: 'Diseña presupuestos profesionales en minutos con nuestra interfaz intuitiva.'
    },
    {
      icon: Send,
      title: 'Envía con un Clic',
      description: 'Envía presupuestos directamente al email de tus clientes con seguimiento.'
    },
    {
      icon: CheckCircle,
      title: 'Aprobación Online',
      description: 'Tus clientes pueden aprobar o rechazar presupuestos desde cualquier dispositivo.'
    },
    {
      icon: TrendingUp,
      title: 'Métricas en Tiempo Real',
      description: 'Visualiza tasas de conversión, tiempos de respuesta y más.'
    },
    {
      icon: Users,
      title: 'Gestión de Clientes',
      description: 'Mantén una base de datos organizada de todos tus clientes.'
    },
    {
      icon: Clock,
      title: 'Tracking de Apertura',
      description: 'Sabe exactamente cuándo tu cliente abre el presupuesto.'
    }
  ];

  const plans = [
    {
      name: 'Starter',
      price: 'Gratis',
      period: '',
      description: 'Perfecto para empezar',
      features: [
        '1 usuario',
        '5 presupuestos activos',
        'Dashboard básico',
        'Envío por email'
      ],
      cta: 'Empezar Gratis',
      popular: false
    },
    {
      name: 'Profesional',
      price: '19€',
      period: '/mes',
      description: 'Para profesionales activos',
      features: [
        'Hasta 5 usuarios',
        'Presupuestos ilimitados',
        'Duplicar presupuestos',
        'Tracking de apertura',
        'KPIs históricos',
        'Branding personalizado'
      ],
      cta: 'Probar 14 días gratis',
      popular: true
    },
    {
      name: 'Equipos',
      price: '39€',
      period: '/mes',
      description: 'Para equipos en crecimiento',
      features: [
        'Usuarios ilimitados',
        'Multi-empresa',
        'Auditoría de eventos',
        'KPIs avanzados',
        'Soporte prioritario',
        'Preparado para facturación'
      ],
      cta: 'Contactar ventas',
      popular: false
    }
  ];

  const handleCTA = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/registro');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Presupuesto Pro
            </span>
          </Link>
          <nav className="flex items-center gap-4">
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700">
                Ir al Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium">
                  Iniciar Sesión
                </Link>
                <Button onClick={() => navigate('/registro')} className="bg-blue-600 hover:bg-blue-700" data-testid="landing-cta-btn">
                  Prueba Gratis
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden noise-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slideUp">
              <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Crea presupuestos
                <span className="text-blue-600"> rápidos</span> y
                <span className="text-blue-600"> profesionales</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
                Deja de crear presupuestos en Word o Excel. Envía cotizaciones profesionales, 
                haz seguimiento de apertura y recibe aprobaciones online.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 h-12 px-8 text-base"
                  onClick={handleCTA}
                  data-testid="hero-cta-btn"
                >
                  Empieza Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="h-12 px-8 text-base"
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver Planes
                </Button>
              </div>
              <p className="mt-4 text-sm text-slate-500">
                No se requiere tarjeta de crédito
              </p>
            </div>
            <div className="relative animate-slideUp stagger-2">
              <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-500">Presupuesto #2024-0042</span>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">Aprobado</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Desarrollo web</span>
                    <span className="font-medium">2.500 €</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Diseño UI/UX</span>
                    <span className="font-medium">800 €</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">SEO inicial</span>
                    <span className="font-medium">450 €</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="font-bold text-xl text-slate-900">4.538,50 €</span>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
                Cliente: TechSolutions SL
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Así de simple funciona
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              En tres sencillos pasos, transforma tu proceso de ventas
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Crear</h3>
              <p className="text-slate-600">Añade cliente, ítems y condiciones en minutos</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-blue-600" />
              </div>
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Enviar</h3>
              <p className="text-slate-600">El cliente recibe un email con enlace único</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aprobar</h3>
              <p className="text-slate-600">Tu cliente aprueba online y tú recibes notificación</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Todo lo que necesitas
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Herramientas potentes para gestionar tus presupuestos de forma profesional
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="card-hover border-slate-200">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Planes y Precios
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tu negocio
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${plan.popular ? 'border-blue-600 border-2 shadow-lg' : 'border-slate-200'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                    Más Popular
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {plan.price}
                    </span>
                    <span className="text-slate-600">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-6 text-left">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={handleCTA}
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Empieza a crear presupuestos profesionales hoy
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            Únete a miles de profesionales que ya usan Presupuesto Pro para gestionar sus cotizaciones
          </p>
          <Button 
            size="lg" 
            className="mt-8 bg-blue-600 hover:bg-blue-700 h-12 px-8 text-base"
            onClick={handleCTA}
            data-testid="footer-cta-btn"
          >
            Prueba Gratis 14 Días
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <span className="text-slate-400 text-sm">© 2024 Presupuesto Pro</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">Términos</a>
            <a href="#" className="hover:text-white transition-colors">Privacidad</a>
            <a href="#" className="hover:text-white transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
